<template>
    <div v-if="isCartOpen" class="cart-overlay" @click="closeCart">
        <div class="cart-panel" @click.stop>
            <!-- Cart Header -->
            <div class="cart-header">
                <div class="cart-title-section">
                    <h2 class="cart-title">Shopping Cart</h2>
                </div>
                <div class="cart-close-btn" @click="closeCart">
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_2165_415)">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M7.41765 0.684155L4.50083 3.60097L1.58402 0.684155C1.31518 0.415322 0.88009 0.415808 0.611743 0.684155C0.343397 0.952502 0.34291 1.38759 0.611743 1.65643L3.52856 4.57324L0.611743 7.49006C0.343396 7.7584 0.34291 8.1935 0.611743 8.46233C0.880577 8.73116 1.31567 8.73068 1.58402 8.46233L4.50083 5.54551L7.41765 8.46233C7.68648 8.73116 8.12157 8.73068 8.38992 8.46233C8.65826 8.19398 8.65875 7.75889 8.38992 7.49006L5.4731 4.57324L8.38992 1.65643C8.65827 1.38808 8.65875 0.952988 8.38992 0.684155C8.12108 0.415322 7.68599 0.415808 7.41765 0.684155Z"
                                fill="black" />
                        </g>
                        <defs>
                            <clipPath id="clip0_2165_415">
                                <rect width="9" height="9" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>

                </div>
            </div>

            <!-- Cart Content -->
            <div class="cart-content">
                <!-- Cart Items -->
                <div v-if="hasItems" class="cart-items">
                    <div v-for="item in cartItems" :key="item._id" class="cart-item">
                        <div class="cart-item-image">
                            <img v-if="item.product.media && item.product.media.length > 0"
                                :src="item.product.media[0].url"
                                :alt="item.product.media[0].alt || item.product.title" />
                            <div v-else class="cart-item-placeholder">
                                <div class="placeholder-icon">📦</div>
                            </div>
                        </div>

                        <div class="cart-item-details">
                            <div class="cart-item-info">
                                <h4 class="cart-item-title">{{ item.product.talent?.name || item.product.title }}</h4>
                                <p v-if="item.selectedSize" class="cart-item-size">Size: {{ item.selectedSize.label }}
                                </p>
                                <div class="cart-item-price">
                                    {{ formatCartItemPrice(item) }}
                                </div>
                            </div>

                            <div class="cart-item-controls">
                                <div class="quantity-controls">
                                    <button class="quantity-btn decrease" @click="decreaseQuantity(item)"
                                        :disabled="item.quantity <= 1">
                                        -
                                    </button>
                                    <div class="quantity-display">{{ item.quantity }}</div>
                                    <button class="quantity-btn increase" @click="increaseQuantity(item)"
                                        :disabled="!!(item.selectedSize?.stockQuantity && item.quantity >= item.selectedSize.stockQuantity)">
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty Cart State -->
                <div v-else class="empty-cart">
                    <div class="empty-cart-icon">🛒</div>
                    <h3 class="empty-cart-title">Your cart is empty</h3>
                    <p class="empty-cart-text">Add some products to get started</p>
                </div>
            </div>

            <!-- Cart Footer -->
            <div v-if="hasItems" class="cart-footer">
                <div class="cart-summary">
                    <div class="subtotal-row">
                        <span class="subtotal-label">Subtotal</span>
                        <span class="subtotal-amount">{{ getCurrencySymbol(cartCurrency) }}{{ formatPrice(cartSubtotal)
                            }}</span>
                    </div>

                    <button class="checkout-btn" @click="proceedToCheckout">
                        PROCEED TO CHECKOUT
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useCartStore } from '~/composables/useCartStore'
import { useRouter } from 'vue-router'
const router = useRouter()
const {
    cartItems,
    isCartOpen,
    hasItems,
    cartSubtotal,
    cartCurrency,
    closeCart,
    updateQuantity,
    getCurrencySymbol,
    formatPrice,
    getProductPrice,
    getCartForCheckout
} = useCartStore()

const formatCartItemPrice = (item: any) => {
    const price = getProductPrice(item.product, item.selectedSize)
    if (price) {
        const finalPrice = price.isOnSale ? price.salePrice : price.basePrice
        return `${getCurrencySymbol(price.currency)}${formatPrice(finalPrice)}`
    }
    return 'Price not available'
}

const decreaseQuantity = (item: any) => {
    try {
        updateQuantity(item._id, item.quantity - 1)
    } catch (error: any) {
        alert(error.message)
    }
}

const increaseQuantity = (item: any) => {
    try {
        updateQuantity(item._id, item.quantity + 1)
    } catch (error: any) {
        alert(error.message)
    }
}

const proceedToCheckout = () => {
    // Navigate to checkout page
    router.push('/checkout')
    closeCart()
}
</script>

<style scoped>
.cart-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    display: flex;
    justify-content: flex-end;
}

.cart-panel {
    width: 390px;
    height: 100vh;
    background: #ffffff;
    border-left: 1px solid #000;
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    animation: slideIn 0.3s ease-out forwards;
}

@keyframes slideIn {
    to {
        transform: translateX(0);
    }
}

.cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #000;
    background: #ffffff;
}

.cart-title-section {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cart-title {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.3;
    color: #000000;
    margin: 0;
}

.cart-content {
    overflow-y: auto;
    padding: 0;
}

.cart-items {
    display: flex;
    flex-direction: column;
}

.cart-item {
    display: flex;
    gap: 24px;
    padding: 20px;
    border-bottom: 1px solid #f0f0f0;
    width: 100%;
}

.cart-item:last-child {
    border-bottom: none;
}

.cart-item-image {
    width: 50%;
    height: auto;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 4px;
}

.cart-item-image img {
    aspect-ratio: 164/177;
    width: 100%;
    object-fit: contain;
}

.cart-item-placeholder {
    width: 100%;
    height: 100%;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
}

.placeholder-icon {
    font-size: 24px;
    opacity: 0.4;
}

.cart-item-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.cart-item-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.cart-item-title {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.12;
    color: #000000;
    margin: 0;
}

.cart-item-artist {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.3;
    color: #000000;
    margin: 0;
}

.cart-item-size {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 10px;
    line-height: 1.3;
    color: #666666;
    margin: 0;
}

.cart-item-price {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.3;
    color: #B8B8B8;
}

.cart-item-controls {
    display: flex;
    justify-content: flex-start;
    align-items: center;
}

.quantity-controls {
    display: flex;
    align-items: center;
    gap: 5px;
}

.quantity-btn {
    width: 25px;
    height: 25px;
    aspect-ratio: 1;
    border: 0.5px solid #000;
    border-radius: 499.5px;
    background: white;
    color: #000;
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 5px 10px;
}

.quantity-btn:hover:not(:disabled) {
    background: #f0f0f0;
}

.quantity-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.quantity-display {
    width: 60px;
    height: 25px;
    border: 0.5px solid #000;
    border-radius: 499.5px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 8px;
    color: #000;
    padding: 5px 10px;
}

.empty-cart {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    height: 100%;
}

.empty-cart-icon {
    font-size: 48px;
    margin-bottom: 20px;
    opacity: 0.3;
}

.empty-cart-title {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 18px;
    color: #000000;
    margin: 0 0 10px 0;
}

.empty-cart-text {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: #666666;
    margin: 0;
}

.cart-footer {
    border-top: 1px solid #f0f0f0;
    background: #ffffff;
    padding: 20px;
}

.cart-summary {
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 350px;
}

.subtotal-row {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
}

.subtotal-label {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.12;
    color: #000000;
}

.subtotal-amount {
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.3;
    color: #B8B8B8;
}

.checkout-btn {
    width: 100%;
    max-width: 350px;
    padding: 0 20px;
    height: 31px;
    border: 1px solid #000;
    border-radius: 999px;
    background: #000;
    color: #fff;
    font-family: 'ABC Walter Neue', Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.89;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
}

.checkout-btn:hover {
    background: #333;
}

/* Mobile Styles */
@media (max-width: 768px) {
    .cart-panel {
        width: 100vw;
        max-width: 390px;
    }

    .cart-item {
        gap: 16px;
    }

    .cart-summary {
        max-width: 100%;
    }

    .checkout-btn {
        max-width: 100%;
    }
}
</style>